import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  LayoutDashboard,
  Inbox,
  Calendar,
  FileText,
  Mail,
  Download,
  Trash2,
  Plus,
  Loader2,
  Pencil,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  KeyRound,
  X,
} from "lucide-react";
import { api, KYVIXE, API_BASE } from "@/lib/api";

const TABS = [
  { id: "overview", label: "overview", icon: LayoutDashboard },
  { id: "leads", label: "leads", icon: Inbox },
  { id: "bookings", label: "bookings", icon: Calendar },
  { id: "blog", label: "blog", icon: FileText },
  { id: "newsletter", label: "newsletter", icon: Mail },
  { id: "settings", label: "settings", icon: SettingsIcon },
];

const STATUS_OPTIONS = ["New", "Contacted", "Booked", "Closed"];

function Stat({ label, value, hint }) {
  return (
    <div className="kyv-card p-6">
      <p className="text-xs uppercase tracking-widest text-gray-500">{label}</p>
      <p className="mt-2 font-display font-bold text-3xl tracking-tight">{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function StatusPill({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-white focus:outline-none focus:border-blue-500/60"
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s} value={s} className="bg-[#12121a]">
          {s}
        </option>
      ))}
    </select>
  );
}

function Overview({ stats, leads, bookings }) {
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="leads this month" value={stats?.leads_month ?? "—"} hint={`${stats?.leads_total ?? 0} total`} />
        <Stat label="bookings this month" value={stats?.bookings_month ?? "—"} hint={`${stats?.bookings_total ?? 0} total`} />
        <Stat label="posts published" value={stats?.posts_published ?? "—"} hint={`${stats?.posts_total ?? 0} total posts`} />
        <Stat label="newsletter subs" value={stats?.newsletter_subs ?? "—"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="kyv-card p-6">
          <h3 className="font-display lowercase font-bold text-xl mb-4">recent leads</h3>
          {leads.length === 0 ? (
            <p className="text-sm text-gray-500">no leads yet.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {leads.slice(0, 5).map((l) => (
                <li key={l.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{l.name}</p>
                    <p className="text-xs text-gray-500 truncate">{l.email} · {l.service}</p>
                  </div>
                  <span className="text-xs text-blue-400 shrink-0">{l.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="kyv-card p-6">
          <h3 className="font-display lowercase font-bold text-xl mb-4">upcoming bookings</h3>
          {bookings.length === 0 ? (
            <p className="text-sm text-gray-500">no bookings yet.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {bookings.slice(0, 5).map((b) => (
                <li key={b.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{b.name}</p>
                    <p className="text-xs text-gray-500 truncate">{b.date} · {b.time} · {b.service}</p>
                  </div>
                  <span className="text-xs text-blue-400 shrink-0">{b.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ExportBtn({ href, label }) {
  const token = localStorage.getItem("kyvixe_token");
  const dl = async () => {
    try {
      const r = await fetch(`${API_BASE}${href}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = href.split("/").pop() + ".csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export");
    }
  };
  return (
    <button onClick={dl} className="kyv-btn-ghost text-xs inline-flex items-center gap-2">
      <Download size={14} /> {label}
    </button>
  );
}

function LeadsTable({ leads, onUpdate, onDelete }) {
  const [filter, setFilter] = React.useState("");
  const filtered = filter ? leads.filter((l) => l.status === filter) : leads;
  return (
    <div className="space-y-4" data-testid="admin-leads-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {["", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                filter === s
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "border-white/10 text-gray-300 hover:border-blue-500/60"
              }`}
              data-testid={`leads-filter-${s || "all"}`}
            >
              {(s || "all").toLowerCase()}
            </button>
          ))}
        </div>
        <ExportBtn href="/admin/leads/export" label="export csv" />
      </div>
      <div className="kyv-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-left text-gray-400 text-xs uppercase tracking-widest bg-white/5">
              <tr>
                <th className="px-4 py-3">name</th>
                <th className="px-4 py-3">contact</th>
                <th className="px-4 py-3">service</th>
                <th className="px-4 py-3">message</th>
                <th className="px-4 py-3">status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">no leads.</td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="hover:bg-white/5" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{l.name}</p>
                    <p className="text-xs text-gray-500">{new Date(l.created_at).toLocaleString("en", {dateStyle: "short", timeStyle: "short"})}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p>{l.email}</p>
                    {l.phone && <p className="text-xs text-gray-500">{l.phone}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{l.service}</td>
                  <td className="px-4 py-3 text-gray-400 max-w-xs">
                    <p className="line-clamp-2">{l.message}</p>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={l.status} onChange={(v) => onUpdate(l.id, v)} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onDelete(l.id)}
                      className="text-gray-400 hover:text-red-400"
                      data-testid={`lead-delete-${l.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BookingsTable({ bookings, onUpdate, onDelete }) {
  const [filter, setFilter] = React.useState("");
  const filtered = filter ? bookings.filter((b) => b.status === filter) : bookings;
  return (
    <div className="space-y-4" data-testid="admin-bookings-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {["", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                filter === s
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "border-white/10 text-gray-300 hover:border-blue-500/60"
              }`}
            >
              {(s || "all").toLowerCase()}
            </button>
          ))}
        </div>
        <ExportBtn href="/admin/bookings/export" label="export csv" />
      </div>
      <div className="kyv-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-left text-gray-400 text-xs uppercase tracking-widest bg-white/5">
              <tr>
                <th className="px-4 py-3">when</th>
                <th className="px-4 py-3">customer</th>
                <th className="px-4 py-3">service</th>
                <th className="px-4 py-3">notes</th>
                <th className="px-4 py-3">status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">no bookings.</td>
                </tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} className="hover:bg-white/5" data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{b.date}</p>
                    <p className="text-xs text-blue-400">{b.time}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{b.name}</p>
                    <p className="text-xs text-gray-500">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{b.service}</td>
                  <td className="px-4 py-3 text-gray-400 max-w-xs">
                    <p className="line-clamp-2">{b.notes || "—"}</p>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={b.status} onChange={(v) => onUpdate(b.id, v)} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onDelete(b.id)}
                      className="text-gray-400 hover:text-red-400"
                      data-testid={`booking-delete-${b.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const EMPTY_POST = { title: "", excerpt: "", content: "", category: "General", image_url: "", published: false, slug: "" };

function BlogEditor({ open, initial, onClose, onSaved }) {
  const [form, setForm] = React.useState(EMPTY_POST);
  const [saving, setSaving] = React.useState(false);
  const editing = Boolean(initial?.id);

  React.useEffect(() => {
    if (open) setForm({ ...EMPTY_POST, ...initial });
  }, [open, initial]);

  if (!open) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/blog/${initial.id}`, form);
        toast.success("post updated");
      } else {
        await api.post("/admin/blog", form);
        toast.success("post created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="blog-editor-modal">
      <form onSubmit={save} className="kyv-card w-full max-w-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display lowercase font-bold text-2xl">
            {editing ? "edit post" : "new post"}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="blog-editor-close">
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-400 lowercase">title *</label>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60" data-testid="blog-editor-title" />
          </div>
          <div>
            <label className="text-xs text-gray-400 lowercase">category</label>
            <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60" />
          </div>
          <div>
            <label className="text-xs text-gray-400 lowercase">slug (optional)</label>
            <input value={form.slug || ""} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="auto from title" className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-400 lowercase">image url</label>
            <input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://…" className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60" data-testid="blog-editor-image" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-400 lowercase">excerpt</label>
            <textarea rows={2} value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-400 lowercase">content (html supported) *</label>
            <textarea required rows={10} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500/60" data-testid="blog-editor-content" />
          </div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-200">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="blog-editor-published" />
            publish immediately
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="kyv-btn-ghost text-sm">cancel</button>
          <button type="submit" disabled={saving} className="kyv-btn-primary text-sm inline-flex items-center gap-2" data-testid="blog-editor-save">
            {saving && <Loader2 size={14} className="animate-spin" />} save
          </button>
        </div>
      </form>
    </div>
  );
}

function BlogTab({ posts, refresh }) {
  const [editor, setEditor] = React.useState({ open: false, initial: null });
  const togglePublish = async (p) => {
    await api.patch(`/admin/blog/${p.id}`, { ...p, published: !p.published });
    toast.success(p.published ? "unpublished" : "published");
    refresh();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/blog/${p.id}`);
    toast.success("deleted");
    refresh();
  };

  return (
    <div className="space-y-4" data-testid="admin-blog-tab">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-display lowercase font-bold text-2xl">blog posts</h2>
          <p className="text-sm text-gray-500">{posts.length} total · {posts.filter((p) => p.published).length} published</p>
        </div>
        <button
          onClick={() => setEditor({ open: true, initial: null })}
          className="kyv-btn-primary text-sm inline-flex items-center gap-2"
          data-testid="admin-blog-new-btn"
        >
          <Plus size={14} /> new post
        </button>
      </div>
      <div className="kyv-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-left text-gray-400 text-xs uppercase tracking-widest bg-white/5">
              <tr>
                <th className="px-4 py-3">title</th>
                <th className="px-4 py-3">category</th>
                <th className="px-4 py-3">status</th>
                <th className="px-4 py-3">updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {posts.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">no posts yet.</td></tr>
              )}
              {posts.map((p) => (
                <tr key={p.id} className="hover:bg-white/5">
                  <td className="px-4 py-3">
                    <p className="font-medium">{p.title}</p>
                    <p className="text-xs text-gray-500">/{p.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{p.category}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.published ? "bg-blue-500/10 text-blue-400" : "bg-white/5 text-gray-400"}`}>
                      {p.published ? "published" : "draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{new Date(p.updated_at || p.created_at).toLocaleString("en", {dateStyle: "short"})}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => togglePublish(p)} className="text-gray-400 hover:text-blue-400" data-testid={`blog-toggle-${p.id}`}>
                        {p.published ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button onClick={() => setEditor({ open: true, initial: p })} className="text-gray-400 hover:text-blue-400" data-testid={`blog-edit-${p.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => remove(p)} className="text-gray-400 hover:text-red-400" data-testid={`blog-delete-${p.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <BlogEditor
        open={editor.open}
        initial={editor.initial}
        onClose={() => setEditor({ open: false, initial: null })}
        onSaved={() => {
          setEditor({ open: false, initial: null });
          refresh();
        }}
      />
    </div>
  );
}

function SettingsTab({ email, onLogout }) {
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirmPw, setConfirmPw] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirmPw) {
      toast.error("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Please sign in again.");
      setCurrent("");
      setNext("");
      setConfirmPw("");
      // force re-auth with the new password
      setTimeout(onLogout, 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-settings-tab">
      <div>
        <h2 className="font-display lowercase font-bold text-2xl">settings</h2>
        <p className="text-sm text-gray-500">signed in as {email}</p>
      </div>

      <div className="kyv-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={16} className="text-blue-400" />
          <h3 className="font-display lowercase font-bold text-xl">change password</h3>
        </div>
        <p className="text-xs text-gray-500 mb-5">
          use at least 8 characters. you'll be signed out and asked to sign in
          with the new password.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <label className="text-xs text-gray-400 lowercase">current password *</label>
            <input
              type={showPw ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 lowercase">new password *</label>
            <input
              type={showPw ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 lowercase">confirm new password *</label>
            <input
              type={showPw ? "text" : "password"}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
              data-testid="confirm-password-input"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400">
            <input
              type="checkbox"
              checked={showPw}
              onChange={(e) => setShowPw(e.target.checked)}
              data-testid="show-password-toggle"
            />
            show passwords
          </label>
          <button
            type="submit"
            disabled={saving}
            className="kyv-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="change-password-submit"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} update password
          </button>
        </form>
      </div>
    </div>
  );
}

function NewsletterTab({ subs }) {
  return (
    <div className="space-y-4" data-testid="admin-newsletter-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display lowercase font-bold text-2xl">newsletter subscribers</h2>
        <ExportBtn href="/admin/newsletter/export" label="export csv" />
      </div>
      <div className="kyv-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <thead className="text-left text-gray-400 text-xs uppercase tracking-widest bg-white/5">
              <tr>
                <th className="px-4 py-3">email</th>
                <th className="px-4 py-3">subscribed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {subs.length === 0 && (
                <tr><td colSpan={2} className="px-4 py-10 text-center text-gray-500">no subscribers yet.</td></tr>
              )}
              {subs.map((s) => (
                <tr key={s.id} className="hover:bg-white/5">
                  <td className="px-4 py-3">{s.email}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{new Date(s.created_at).toLocaleString("en", {dateStyle: "short", timeStyle: "short"})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = React.useState("overview");
  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState(null);
  const [leads, setLeads] = React.useState([]);
  const [bookings, setBookings] = React.useState([]);
  const [posts, setPosts] = React.useState([]);
  const [subs, setSubs] = React.useState([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, b, p, n] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/bookings"),
        api.get("/admin/blog"),
        api.get("/admin/newsletter"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setBookings(b.data);
      setPosts(p.data);
      setSubs(n.data);
    } catch (e) {
      // 401 handled by interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!localStorage.getItem("kyvixe_token")) {
      nav("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, nav]);

  const logout = () => {
    localStorage.removeItem("kyvixe_token");
    nav("/admin/login", { replace: true });
  };

  const updateLead = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
  };
  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((rows) => rows.filter((r) => r.id !== id));
    load();
  };
  const updateBooking = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    setBookings((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/admin/bookings/${id}`);
    setBookings((rows) => rows.filter((r) => r.id !== id));
    load();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-display text-lg font-bold lowercase">kyvixe<span className="text-blue-500">.</span> admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:block text-xs text-gray-400">{localStorage.getItem("kyvixe_email")}</span>
            <button onClick={logout} className="kyv-btn-ghost text-sm inline-flex items-center gap-2" data-testid="admin-logout-btn">
              <LogOut size={14} /> sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <nav className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition ${
                tab === t.id
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "border-white/10 text-gray-300 hover:border-blue-500/60"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </nav>

        {loading ? (
          <div className="flex justify-center py-24 text-gray-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            {tab === "overview" && <Overview stats={stats} leads={leads} bookings={bookings} />}
            {tab === "leads" && <LeadsTable leads={leads} onUpdate={updateLead} onDelete={deleteLead} />}
            {tab === "bookings" && <BookingsTable bookings={bookings} onUpdate={updateBooking} onDelete={deleteBooking} />}
            {tab === "blog" && <BlogTab posts={posts} refresh={load} />}
            {tab === "newsletter" && <NewsletterTab subs={subs} />}
            {tab === "settings" && (
              <SettingsTab
                email={localStorage.getItem("kyvixe_email") || ""}
                onLogout={logout}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
